import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import type { PromotionDto, PromotionInput } from "@school-sales-ai/shared";
import { api } from "@/lib/api";
import { useScopedClientId } from "@/hooks/useScopedClientId";

const BASE = "/crm/promotions";

/**
 * The promotion list.
 *
 * Keyed on the scoped client, never on `activeClientId` — a super-admin using
 * the client switcher must not see one tenant's price list under another's name.
 * `client-scope.guard.test.ts` enforces this structurally for every hook here.
 */
export function usePromotions() {
  const clientId = useScopedClientId();
  return useQuery({
    queryKey: ["promotions", clientId],
    queryFn: () => api.get<{ promotions: PromotionDto[] }>(BASE).then((r) => r.promotions),
    enabled: !!clientId,
  });
}

/**
 * Every promotion mutation shares this shape.
 *
 * Invalidating `catalog-context` alongside the list is the point: a promotion
 * IS a price, so the agent-context preview the CRM shows is stale the moment one
 * is saved. The backend drops its own 5-minute cache in the same breath
 * (`aiService.invalidateCatalogContext`); this is the browser's half.
 */
function usePromotionAction<TVars>(
  mutationFn: (vars: TVars) => Promise<PromotionDto>,
  successMessage: string,
) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["promotions"] });
      void queryClient.invalidateQueries({ queryKey: ["catalog-context"] });
      toast.success(successMessage);
    },
    onError: (err: Error) => toast.error(err.message),
  });
}

export function useCreatePromotion() {
  return usePromotionAction(
    (input: PromotionInput) => api.post<PromotionDto>(BASE, input),
    "Promoción creada",
  );
}

export function useUpdatePromotion() {
  return usePromotionAction(
    ({ id, input }: { id: string; input: PromotionInput }) =>
      api.put<PromotionDto>(`${BASE}/${id}`, input),
    "Promoción actualizada",
  );
}

/**
 * Switches a promotion on or off. There is no delete: `order_items.promotion_id`
 * is RESTRICT, and those are financial records.
 */
export function useSetPromotionActive() {
  return usePromotionAction(
    ({ id, isActive }: { id: string; isActive: boolean }) =>
      api.patch<PromotionDto>(`${BASE}/${id}/active`, { isActive }),
    "Promoción actualizada",
  );
}
